// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ReportPageScaffold from '../components/ReportPageScaffold';
import DateRangeFilter from '../components/DateRangeFilter';
import PaginationFooter from '../components/PaginationFooter';
import { ReportLoadingState } from '../components/ReportLoadingState';
import { useState, useEffect, useMemo } from 'react';
import { Download, SlidersHorizontal, MessageSquare } from 'lucide-react';
import DashboardFilterModal, { type FilterSection } from '../components/dashboard/DashboardFilterModal';
import { useStoreRegions } from '../hooks/useStoreRegions';
import EmployeeRemarksModal from '../components/EmployeeRemarksModal';
import { moodReportAPI, employeeRemarksAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import CustomReportPanel from '../components/CustomReportPanel';
import { PAGE_SIZE_OPTIONS } from '../constants/reportPageSize';
import { buildCsv, downloadCsv } from '../utils/csv';
import { usePagination } from '../hooks/usePagination';
import { useReportFilters } from '../hooks/useReportFilters';
import { useLocation } from 'react-router-dom';
import { readDrillParams } from '../utils/drilldown';
import { getLocalYesterdayDateString } from '../utils/dateHelpers';
import { isAdminForCurrentProject } from '../utils/isAdminForCurrentProject';
import { useTableSort, SortTh, type SortAccessors } from '../components/dashboard/useTableSort';


interface MoodRecord {
  id: number;
  empCode: string;
  employeeName: string;
  reportingHead: string;
  storeName: string;
  storeCode: string;
  storeCategory: string;
  trade: string;
  city: string;
  state: string;
  selectionDate: string;
  emoji: string;
  emojiText: string;
  createdAt: string;
}

// Dedupe + sort the values powering a filter dropdown (drops blanks).
const uniqSorted = (values: (string | undefined | null)[]): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v && v.trim() !== '')))
    .sort((a, b) => a.localeCompare(b));

const MOOD_FILTERS = [
  { value: 'all', label: 'All moods' },
  { value: '😄', label: '😄 Very Happy' },
  { value: '🙂', label: '🙂 Happy' },
  { value: '😐', label: '😐 Neutral' },
  { value: '🙁', label: '🙁 Unhappy' },
  { value: '😫', label: '😫 Very Unhappy' },
];

const getMoodBadgeClass = (emoji: string): string => {
  switch (emoji) {
    case '😄': return 'bg-emerald-100 text-emerald-800';
    case '🙂': return 'bg-green-100 text-green-800';
    case '😐': return 'bg-yellow-100 text-yellow-800';
    case '🙁': return 'bg-orange-100 text-orange-800';
    case '😫': return 'bg-red-100 text-red-800';
    default:   return 'bg-gray-100 text-gray-700';
  }
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// ── Column sorting ───────────────────────────────────────────────────────────
// MOOD sorts along the mood scale (Very Happy → Very Unhappy) — A–Z on these labels
// would interleave the two extremes. Keyed on the chip's label, since both mobile
// home screens save the same labels but different "Very Unhappy" emoji (😫 / 😡);
// the emoji is only a fallback for a row saved without a label.
const MOOD_SCALE: Record<string, number> = {
  'very happy': 5, happy: 4, neutral: 3, unhappy: 2, 'very unhappy': 1,
  '😄': 5, '🙂': 4, '😐': 3, '🙁': 2, '😫': 1, '😡': 1,
};

// One accessor per sortable column — each returns the value its cell shows. Defined
// once at module level so the sort isn't re-run on every unrelated re-render (page
// flips, remark-count loads) of what can be a many-thousand-row date range.
const SORT_ACCESSORS: SortAccessors<MoodRecord> = {
  date: (r) => (r.selectionDate ? formatDate(r.selectionDate) : null), // YYYY-MM-DD, as shown
  empCode: (r) => r.empCode,
  employeeName: (r) => r.employeeName,
  reportingHead: (r) => r.reportingHead,
  storeName: (r) => r.storeName,
  storeCode: (r) => r.storeCode,
  city: (r) => r.city,
  mood: (r) => MOOD_SCALE[(r.emojiText || '').trim().toLowerCase()] ?? MOOD_SCALE[r.emoji] ?? null,
};

export default function MoodReportPage() {
  const { user, selectedProjectId } = useAuthStore();
  const [moodData, setMoodData] = useState<MoodRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Per-employee remarks (call notes) — a generic thread per employee, opened from
  // any row regardless of its date. `remarkCounts` drives the "N notes" badge.
  const [remarksFor, setRemarksFor] = useState<{ code: string; name: string } | null>(null);
  const [remarkCounts, setRemarkCounts] = useState<Record<string, number>>({});

  // Pending → applied date model + pagination state are shared verbatim with
  // the other report pages — see hooks/useReportFilters. Editing the inputs
  // only updates pending state; only Apply commits the values into the applied
  // copy that the fetch effect watches, keeping the heavy backend query from
  // firing on every date-picker keystroke.
  // Default both From and To to yesterday — most users open this report the
  // morning after to review the previous day's mood selections. Matches the
  // landing-page default of Daily Attendance / Random Attendance / Task Response.
  // DEEP LINK (dashboard drill-down): ?manager=<name>&startDate=&endDate=
  // Seeded through useReportFilters' initializers (pending AND applied in one render)
  // so the linked range fetches immediately without an Apply click or a double fetch.
  const location = useLocation();
  const drill = useMemo(() => readDrillParams(location.search), [location.search]);

  const {
    startDate, setStartDate,
    endDate, setEndDate,
    appliedStartDate, appliedEndDate,
    searchQuery, setSearchQuery,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    hasPendingDateChanges,
    applyDateRange,
  } = useReportFilters({
    initialStartDate: drill.startDate || getLocalYesterdayDateString(),
    initialEndDate: drill.endDate || getLocalYesterdayDateString(),
    initialSearchQuery: drill.employeeCode || drill.q || '',
  });
  const [emojiFilter, setEmojiFilter] = useState('all');
  // Store Category / State / City / Reporting Head are filtered client-side from
  // the already-loaded rows (same model as the search box), so they don't trigger
  // a backend refetch and the emoji summary recomputes to match the selection.
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [tradeFilter, setTradeFilter] = useState('all');
  // Region — coarser than State, resolved from the state via the 1Office master.
  const [regionFilter, setRegionFilter] = useState('all');
  // Filters by manager NAME (the rows carry reportingHead as a display name), so the
  // drill-down link passes ?manager=<name>.
  const [reportingHeadFilter, setReportingHeadFilter] = useState(drill.manager || 'all');

  // Dropdown option lists derived from the loaded data set.
  const categoryOptions = useMemo(() => uniqSorted(moodData.map(r => r.storeCategory)), [moodData]);
  const stateOptions = useMemo(() => uniqSorted(moodData.map(r => r.state)), [moodData]);
  const cityOptions = useMemo(() => uniqSorted(moodData.map(r => r.city)), [moodData]);
  const tradeOptions = useMemo(() => uniqSorted(moodData.map(r => r.trade)), [moodData]);
  const { regionOf, regionsFrom } = useStoreRegions();
  const regionOptions = useMemo(() => regionsFrom(moodData.map(r => r.state)), [moodData, regionsFrom]);
  const reportingHeadOptions = useMemo(() => uniqSorted(moodData.map(r => r.reportingHead)), [moodData]);

  // When the dataset reloads (date range / project / emoji change), drop any
  // selected value that no longer exists in the new rows so the report can't
  // silently filter everything out behind a now-orphaned dropdown selection.
  useEffect(() => {
    // Only prune once rows EXIST. On first paint (and on any empty result) the option
    // lists are empty, which would otherwise wipe a deep-linked ?manager= selection
    // before the fetch returns — and would silently clear a user's own filters
    // whenever a date range happened to return nothing.
    if (moodData.length === 0) return;
    if (categoryFilter !== 'all' && !categoryOptions.includes(categoryFilter)) setCategoryFilter('all');
    if (stateFilter !== 'all' && !stateOptions.includes(stateFilter)) setStateFilter('all');
    if (cityFilter !== 'all' && !cityOptions.includes(cityFilter)) setCityFilter('all');
    if (reportingHeadFilter !== 'all' && !reportingHeadOptions.includes(reportingHeadFilter)) setReportingHeadFilter('all');
    if (tradeFilter !== 'all' && !tradeOptions.includes(tradeFilter)) setTradeFilter('all');
    if (regionFilter !== 'all' && !regionOptions.includes(regionFilter)) setRegionFilter('all');
  }, [moodData, categoryOptions, stateOptions, cityOptions, reportingHeadOptions, tradeOptions, regionOptions,
      categoryFilter, stateFilter, cityFilter, reportingHeadFilter, tradeFilter, regionFilter]);

  const fetchMoodReport = async () => {
    setLoading(true);
    try {
      // Send employeeCode only for non-admin callers (see RandomAttendanceLogsPage for context).
      const isAdmin = isAdminForCurrentProject(user, selectedProjectId);

      const response = await moodReportAPI.getReport({
        start_date: appliedStartDate,
        end_date: appliedEndDate,
        employeeCode: isAdmin ? undefined : user?.userName,
        emoji: emojiFilter,
        projectId: selectedProjectId ?? undefined,
      });
      setMoodData(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching mood report:', err);
      setMoodData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMoodReport();
  }, [appliedStartDate, appliedEndDate, emojiFilter, selectedProjectId]);

  // Reset to page 1 when filters narrow the result set.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, emojiFilter, categoryFilter, stateFilter, cityFilter, tradeFilter, regionFilter, reportingHeadFilter, pageSize]);

  const filteredData = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return moodData.filter(r => {
      if (categoryFilter !== 'all' && r.storeCategory !== categoryFilter) return false;
      if (stateFilter !== 'all' && r.state !== stateFilter) return false;
      if (cityFilter !== 'all' && r.city !== cityFilter) return false;
      if (tradeFilter !== 'all' && (r.trade || '') !== tradeFilter) return false;
      if (regionFilter !== 'all' && regionOf(r.state) !== regionFilter) return false;
      if (reportingHeadFilter !== 'all' && r.reportingHead !== reportingHeadFilter) return false;
      if (!q) return true;
      return (
        r.empCode?.toLowerCase().includes(q) ||
        r.employeeName?.toLowerCase().includes(q) ||
        r.reportingHead?.toLowerCase().includes(q) ||
        r.storeName?.toLowerCase().includes(q) ||
        r.storeCode?.toLowerCase().includes(q) ||
        r.storeCategory?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        r.state?.toLowerCase().includes(q) ||
        r.emojiText?.toLowerCase().includes(q)
      );
    });
  }, [moodData, searchQuery, categoryFilter, stateFilter, cityFilter, tradeFilter, regionFilter, regionOf, reportingHeadFilter]);

  // Column sorting runs on the WHOLE filtered set, then pagination slices the
  // result (so remark counts are still fetched for the visible page). A new sort
  // order starts from page 1.
  const sort = useTableSort(filteredData, SORT_ACCESSORS);
  useEffect(() => {
    setCurrentPage(1);
  }, [sort.sortKey, sort.sortDir]);

  const { totalPages, safePage, paginatedData } = usePagination(sort.sorted, currentPage, pageSize);

  // Clear cached counts when the project changes — counts merge across pages, and
  // 0-count codes are absent from the response (GROUP BY), so without this a badge
  // from another project could linger for an emp_code that exists in both.
  useEffect(() => { setRemarkCounts({}); }, [selectedProjectId]);

  // Remark counts for the CURRENTLY VISIBLE page only — moodData can be thousands of
  // rows, so never send them all as a query string; accumulate across pages viewed.
  const pageRemarkCodes = useMemo(
    () => [...new Set(paginatedData.map((r) => r.empCode).filter(Boolean))].join(','),
    [paginatedData],
  );
  useEffect(() => {
    if (!pageRemarkCodes) return;
    let cancelled = false;
    employeeRemarksAPI
      .counts({ employeeCodes: pageRemarkCodes, projectId: selectedProjectId ?? undefined })
      .then((res) => { if (!cancelled) setRemarkCounts((prev) => ({ ...prev, ...(res.data?.data?.counts || {}) })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pageRemarkCodes, selectedProjectId]);

  const summary = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of filteredData) {
      counts[r.emoji] = (counts[r.emoji] || 0) + 1;
    }
    return MOOD_FILTERS.filter(m => m.value !== 'all').map(m => ({
      emoji: m.value,
      label: m.label,
      count: counts[m.value] || 0,
    }));
  }, [filteredData]);

  // The dropdown filters (mood / category / state / city / reporting head) now live
  // inside the shared Filters modal; only the date range + Download stay inline in
  // the search row. Each is a single-select 'select' section.
  const filterSections: FilterSection[] = [
    { kind: 'select', key: 'mood', label: 'Mood', value: emojiFilter, onChange: setEmojiFilter,
      options: MOOD_FILTERS.map((m) => ({ value: m.value, label: m.label })) },
    { kind: 'select', key: 'category', label: 'Store Category', value: categoryFilter, onChange: setCategoryFilter,
      options: [{ value: 'all', label: 'All categories' }, ...categoryOptions.map((c) => ({ value: c, label: c }))] },
    { kind: 'select', key: 'trade', label: 'Store Trade', value: tradeFilter, onChange: setTradeFilter,
      options: [{ value: 'all', label: 'All trades' }, ...tradeOptions.map((t) => ({ value: t, label: t }))] },
    { kind: 'select', key: 'state', label: 'State', value: stateFilter, onChange: setStateFilter,
      options: [{ value: 'all', label: 'All states' }, ...stateOptions.map((s) => ({ value: s, label: s }))] },
    { kind: 'select', key: 'region', label: 'Region', value: regionFilter, onChange: setRegionFilter,
      options: [{ value: 'all', label: 'All regions' }, ...regionOptions.map((r) => ({ value: r, label: r }))] },
    { kind: 'select', key: 'city', label: 'City', value: cityFilter, onChange: setCityFilter,
      options: [{ value: 'all', label: 'All cities' }, ...cityOptions.map((c) => ({ value: c, label: c }))] },
    { kind: 'select', key: 'reportingHead', label: 'Reporting Head', value: reportingHeadFilter, onChange: setReportingHeadFilter,
      options: [{ value: 'all', label: 'All reporting heads' }, ...reportingHeadOptions.map((rh) => ({ value: rh, label: rh }))] },
  ];
  const activeFilterCount = [emojiFilter, categoryFilter, tradeFilter, stateFilter, regionFilter, cityFilter, reportingHeadFilter].filter((v) => v !== 'all').length;
  const clearAllFilters = () => {
    setEmojiFilter('all');
    setCategoryFilter('all');
    setStateFilter('all');
    setCityFilter('all');
    setTradeFilter('all');
    setRegionFilter('all');
    setReportingHeadFilter('all');
  };

  const [exporting, setExporting] = useState(false);

  // Export. Remarks live in a SEPARATE per-employee thread (employee_remarks), not on the
  // mood row — and the page only pre-fetches remark COUNTS for the visible page. So the
  // export fetches remark TEXT for every employee in the FILTERED set (all pages) before
  // building the file. Fail-open: if that call fails we still download, with blank remarks.
  const handleExportCSV = async () => {
    setExporting(true);
    let remarksByCode: Record<string, Array<{ body: string; author: string | null; date: string | null }>> = {};
    try {
      const codes = [...new Set(filteredData.map((r) => r.empCode).filter(Boolean))];
      if (codes.length) {
        const res = await employeeRemarksAPI.bulk({ employeeCodes: codes, projectId: selectedProjectId ?? undefined });
        remarksByCode = (res.data?.data?.remarks || {}) as typeof remarksByCode;
      }
    } catch {
      // fail-open — a remarks outage must not block the report download
    }
    // One employee's thread can hold several notes; flatten to a single cell as
    // "YYYY-MM-DD — note (Author)", newest first, joined by " | " so it stays greppable
    // in Excel. Also export the count so a reader can spot truncation at a glance.
    const remarkCell = (code: string): string =>
      (remarksByCode[code] || [])
        .map((r) => [r.date, r.body, r.author ? `(${r.author})` : ''].filter(Boolean).join(' '))
        .join(' | ');

    const headers = [
      'Employee Code',
      'Employee Name',
      'Reporting Head',
      'Store Name',
      'Store Code',
      'Store Category',
      'City',
      'State',
      'Date',
      'Mood',
      'Mood Label',
      'Remarks',
      'Remarks Count',
    ];

    const csvData = filteredData.map(record => [
      record.empCode,
      record.employeeName,
      record.reportingHead || '-',
      record.storeName || '-',
      record.storeCode || '-',
      record.storeCategory || '-',
      record.city || '-',
      record.state || '-',
      formatDate(record.selectionDate),
      record.emoji,
      record.emojiText || '-',
      remarkCell(record.empCode) || '-',
      String((remarksByCode[record.empCode] || []).length),
    ]);

    downloadCsv(`mood-report-${appliedStartDate}-to-${appliedEndDate}.csv`, buildCsv(headers, csvData));
    setExporting(false);
  };

  return (
    <CustomReportPanel
      sourceKey="mood"
      sourceLabel="Mood"
      promptPlaceholder='e.g. "Distribution of mood emojis across the team this week, as a pie chart"'
    >
    <ReportPageScaffold
      title="Mood Check"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by employee code, name, store, city, mood..."
      searchInputPaddingRight="pr-4"
      filters={
        <>
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition ${
              activeFilterCount > 0 ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title="Open filters"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-bold text-white">{activeFilterCount}</span>
            )}
          </button>

          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            hasPendingDateChanges={hasPendingDateChanges}
            onStartDateChange={(e) => setStartDate(e.target.value)}
            onEndDateChange={(e) => setEndDate(e.target.value)}
            onStartDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onEndDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onApply={applyDateRange}
          />

          <button
            onClick={handleExportCSV}
            disabled={filteredData.length === 0 || exporting}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Preparing…' : 'Download'}
          </button>
        </>
      }
    >
        {!loading && filteredData.length > 0 && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {summary.map(s => (
              <div key={s.emoji} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                <div className="text-2xl">{s.emoji}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label.replace(`${s.emoji} `, '')}</div>
                <div className="text-lg font-semibold text-gray-900">{s.count}</div>
              </div>
            ))}
          </div>
        )}

        {loading && <ReportLoadingState />}

        {!loading && filteredData.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-gray-500">No mood records found</p>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="overflow-hidden mt-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/70">
                  <tr>
                    <SortTh label="DATE" sortKey="date" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <SortTh label="EMPLOYEE CODE" sortKey="empCode" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <SortTh label="EMPLOYEE NAME" sortKey="employeeName" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <SortTh label="REPORTING HEAD" sortKey="reportingHead" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <SortTh label="STORE NAME" sortKey="storeName" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <SortTh label="STORE CODE" sortKey="storeCode" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <SortTh label="CITY" sortKey="city" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <SortTh label="MOOD" sortKey="mood" sort={sort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap">REMARKS</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedData.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {formatDate(record.selectionDate)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.empCode}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                        {record.employeeName || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.reportingHead || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.storeName || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.storeCode || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {record.city || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getMoodBadgeClass(record.emoji)}`}>
                          <span className="text-base leading-none">{record.emoji}</span>
                          {record.emojiText || ''}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <button
                          type="button"
                          onClick={() => setRemarksFor({ code: record.empCode, name: record.employeeName || record.empCode })}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                          title="View / add remarks for this employee"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          {remarkCounts[record.empCode] ? <span className="font-semibold text-primary-600">{remarkCounts[record.empCode]}</span> : 'Add'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 3-column footer: rows-per-page left, page nav truly centered,
            empty spacer right so nothing falls under the fixed SSI /
            "New custom report" widget at bottom-right. */}
        {!loading && filteredData.length > 0 && (
          <PaginationFooter
            safePage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalRows={filteredData.length}
            onPageSizeChange={(value) => setPageSize(value)}
            onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        )}

        <DashboardFilterModal
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          title="Filters"
          sections={filterSections}
          activeCount={activeFilterCount}
          onClearAll={clearAllFilters}
        />

        <EmployeeRemarksModal
          open={!!remarksFor}
          onClose={() => setRemarksFor(null)}
          employeeCode={remarksFor?.code || ''}
          employeeName={remarksFor?.name}
          projectId={selectedProjectId ?? undefined}
          authorName={user?.fullName || undefined}
          onChanged={() => {
            if (remarksFor) setRemarkCounts((p) => ({ ...p, [remarksFor.code]: (p[remarksFor.code] || 0) + 1 }));
          }}
        />
    </ReportPageScaffold>
    </CustomReportPanel>
  );
}
